import React from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, ShoppingCart, Zap, Shield, ArrowRight, Check, Globe, Bell, Users, CreditCard, Headphones, BarChart3, Palette } from 'lucide-react';
import { IPhoneMockup } from '../components/IPhoneMockup';

export const Home = () => {
  const stats = [
    { number: '$99', label: 'Per Month. Everything.' },
    { number: '0%', label: 'Commission Fees' },
    { number: '7-10', label: 'Days to Launch' },
    { number: '24/7', label: 'Support Included' },
  ];

  return (
    <div>
      {/* HERO */}
      <section data-testid="hero-section" style={{ borderBottom: '4px solid #000', position: 'relative', overflow: 'hidden' }}>
        <video autoPlay loop muted playsInline style={{ width: '100%', height: 'auto', display: 'block' }}>
          <source src="https://customer-assets.emergentagent.com/job_23b5f19d-1a39-4e03-91cd-806fad7065f2/artifacts/o4d5b3u4_hf_20260611_130636_dc4c4e30-ad9b-4c31-af55-10b2b6e81b85.mp4" type="video/mp4" />
        </video>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '3rem 1.5rem', background: 'linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.6) 60%, transparent)' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{ display: 'inline-block', padding: '6px 16px', background: '#22c55e', color: '#fff', borderRadius: '4px', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', border: '3px solid #000', boxShadow: '3px 3px 0px 0px #000', marginBottom: '1.25rem' }}>
              $99/MO — APP + WEBSITE INCLUDED
            </div>
            <h1 className="heading-1" style={{ marginBottom: '1rem', color: '#FFD700' }}>
              We Build Your Restaurant Its Own App & Website.
            </h1>
            <p style={{ fontSize: '1.25rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)', marginBottom: '0.75rem', lineHeight: 1.5 }}>
              Built around the ordering system you already use. No new software to learn. No order limits. One flat price.
            </p>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '2rem' }}>
              {['iOS & Android App', 'Branded Website', 'Unlimited Orders', 'Zero Commissions'].map((t, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Check size={18} color="#22c55e" strokeWidth={3} />
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}>{t}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              <Link to="/book-demo" className="btn-primary" style={{ fontSize: '1.125rem', padding: '16px 32px' }}>
                Book a Free Demo
              </Link>
              <Link to="/pricing" className="btn-secondary" style={{ fontSize: '1.125rem', padding: '16px 32px' }}>
                See What's Included
              </Link>
            </div>
          </div>
        </div>
        <div className="scroll-indicator" onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}>
          <span>Scroll</span>
          <div className="scroll-arrow" />
        </div>
      </section>

      {/* STATS BAR */}
      <section style={{ background: '#000', borderBottom: '4px solid #6A0DAD', padding: '2rem 1.5rem' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '2rem' }}>
            {stats.map((stat, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Bangers', cursive", fontSize: '3rem', color: '#FFD700', letterSpacing: '0.03em' }}>{stat.number}</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE PITCH */}
      <section style={{ padding: '5rem 1.5rem', background: '#fff', borderBottom: '4px solid #000' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center' }}>
            <div>
              <div style={{ display: 'inline-block', padding: '4px 12px', background: '#FFD700', border: '3px solid #000', borderRadius: '4px', fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', boxShadow: '2px 2px 0px 0px #000', marginBottom: '1.25rem' }}>HOW IT WORKS</div>
              <h2 style={{ fontFamily: "'Bangers', cursive", fontSize: 'clamp(2rem, 3.5vw, 2.75rem)', color: '#000', letterSpacing: '0.03em', lineHeight: 1.1, marginBottom: '1.25rem' }}>
                We Build Around What You Already Use.
              </h2>
              <p style={{ color: '#444', fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                Already have an ordering page? Great — we take it and wrap it in a beautiful, branded mobile app listed on the App Store and Google Play. Plus a matching website. Your customers keep ordering the same way they always have. You just get more of them.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem' }}>
                {[
                  'No switching platforms or learning new systems',
                  'Your menu, your brand, your colors — 100% yours',
                  'Unlimited orders for one flat monthly price',
                  'We handle all the tech so you focus on the food',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                    <Check size={20} color="#22c55e" strokeWidth={3} style={{ flexShrink: 0, marginTop: '0.1rem' }} />
                    <span style={{ fontWeight: 600, color: '#333', fontSize: '1rem' }}>{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/book-demo" className="btn-primary" style={{ fontSize: '1rem', padding: '14px 28px' }}>
                Book a Free Demo <ArrowRight size={18} style={{ marginLeft: '0.5rem' }} />
              </Link>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <IPhoneMockup videoSrc="/app-demo-sm.mp4" />
            </div>
          </div>
        </div>
      </section>

      {/* 3 STEPS */}
      <section style={{ padding: '5rem 1.5rem', background: '#6A0DAD', borderBottom: '4px solid #000' }}>
        <div className="container">
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '3rem', color: '#FFD700' }}>Live in 3 Simple Steps</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            {[
              { step: '1', title: 'Book Your Demo', description: 'Schedule a quick call. We show you exactly how your restaurant will look in its own app.' },
              { step: '2', title: 'We Build It For You', description: 'We take your existing ordering page and build a branded app & website around it. You don\'t lift a finger.' },
              { step: '3', title: 'Launch & Grow', description: 'Go live in 7-10 days. Listed on iOS & Android. We train your team and handle all the tech.' },
            ].map((item, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#FFD700', border: '4px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontFamily: "'Bangers', cursive", fontSize: '2.5rem', color: '#000', boxShadow: '4px 4px 0px 0px #000' }}>
                  {item.step}
                </div>
                <h3 style={{ fontFamily: "'Bangers', cursive", fontSize: '1.5rem', marginBottom: '0.75rem', color: '#fff', letterSpacing: '0.03em' }}>{item.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, fontWeight: 500 }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="halftone-bg" style={{ padding: '5rem 1.5rem', background: '#FFF9DB', borderBottom: '4px solid #000', position: 'relative' }}>
        <div className="container" style={{ maxWidth: '1000px', position: 'relative', zIndex: 1 }}>
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '0.75rem', color: '#000' }}>Everything Included for $99/mo</h2>
          <p style={{ textAlign: 'center', color: '#444', marginBottom: '3rem', fontWeight: 500, fontSize: '1.05rem' }}>
            No add-ons. No upsells. No hidden fees. Just everything your restaurant needs.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
            {[
              { icon: <Smartphone size={28} />, title: 'iOS & Android App', desc: 'Your brand on every phone. Listed on both app stores.' },
              { icon: <Globe size={28} />, title: 'Branded Website', desc: 'A matching ordering website that works on any device.' },
              { icon: <ShoppingCart size={28} />, title: 'Unlimited Orders', desc: 'No caps. No per-order fees. Process as many as you want.' },
              { icon: <Zap size={28} />, title: 'Built Around Your System', desc: 'We wrap your current ordering page. Zero retraining.' },
              { icon: <Shield size={28} />, title: 'Zero Commissions', desc: 'Keep 100% of your revenue. No middleman.' },
              { icon: <Bell size={28} />, title: 'Push Notifications', desc: 'Bring customers back with instant promotions.' },
              { icon: <Users size={28} />, title: 'Customer Database', desc: 'Own your customer data and build direct relationships.' },
              { icon: <CreditCard size={28} />, title: 'Payment Processing', desc: 'Cards, Apple Pay, Google Pay — all built in.' },
              { icon: <BarChart3 size={28} />, title: 'Analytics Dashboard', desc: 'Track orders, revenue, and customer behavior.' },
              { icon: <Palette size={28} />, title: 'Custom Branding', desc: 'Your logo, your colors, your restaurant — 100% branded.' },
              { icon: <Headphones size={28} />, title: '24/7 Support', desc: 'We handle the tech so you can focus on the food.' },
              { icon: <ArrowRight size={28} />, title: 'Free Setup & Launch', desc: 'We build it, train your team, and get you live fast.' },
            ].map((f, i) => (
              <div key={i} className="comic-card" style={{ padding: '1.25rem' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: '#FFD700', border: '3px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.75rem', boxShadow: '3px 3px 0px 0px #000' }}>
                  {f.icon}
                </div>
                <h3 style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '0.35rem', textTransform: 'uppercase' }}>{f.title}</h3>
                <p style={{ color: '#555', lineHeight: 1.5, fontWeight: 500, fontSize: '0.9rem' }}>{f.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to="/pricing" className="btn-primary" style={{ fontSize: '1.05rem', padding: '14px 32px' }}>
              See Full Pricing Details <ArrowRight size={18} style={{ marginLeft: '0.5rem' }} />
            </Link>
          </div>
        </div>
      </section>

      {/* PAIN vs SOLUTION */}
      <section style={{ padding: '5rem 1.5rem', background: '#fff', borderBottom: '4px solid #000' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '3rem', color: '#000' }}>
            Stop Giving Away Your Profits
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            <div className="comic-card" style={{ borderColor: '#FF3B30', boxShadow: '8px 8px 0px 0px #FF3B30' }}>
              <h3 style={{ fontFamily: "'Bangers', cursive", fontSize: '1.75rem', marginBottom: '1.5rem', color: '#FF3B30', letterSpacing: '0.03em' }}>Without HungrMates</h3>
              {[
                'Paying 15-30% commission on every order',
                'Delivery apps own your customer data',
                'No way to bring customers back directly',
                'Competing against chains with huge budgets',
              ].map((point, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ flexShrink: 0, width: '24px', height: '24px', borderRadius: '4px', background: '#FF3B30', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.8rem', fontWeight: 900, border: '2px solid #000' }}>&#10005;</div>
                  <p style={{ color: '#333', lineHeight: 1.5, fontWeight: 500 }}>{point}</p>
                </div>
              ))}
            </div>
            <div className="comic-card" style={{ borderColor: '#22c55e', boxShadow: '8px 8px 0px 0px #22c55e' }}>
              <h3 style={{ fontFamily: "'Bangers', cursive", fontSize: '1.75rem', marginBottom: '1.5rem', color: '#22c55e', letterSpacing: '0.03em' }}>With HungrMates</h3>
              {[
                'Keep 100% of every order — zero commissions',
                'You own your customer database',
                'Push notifications bring them back for free',
                'Your own branded app levels the playing field',
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <Check size={24} color="#22c55e" strokeWidth={3} style={{ flexShrink: 0 }} />
                  <p style={{ color: '#333', lineHeight: 1.5, fontWeight: 600 }}>{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section style={{ padding: '5rem 1.5rem', background: '#FFF9DB', borderBottom: '4px solid #000' }}>
        <div className="container">
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '3rem', color: '#000' }}>What Restaurant Owners Say</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2.5rem' }}>
            {[
              { name: 'Maria Rodriguez', restaurant: 'Casa Maria Mexican Grill', quote: 'Since launching our HungrMates app, we\'ve seen a 35% increase in repeat customers. No more paying DoorDash commissions!' },
              { name: 'John Chen', restaurant: 'Chen\'s Noodle House', quote: 'We didn\'t have to learn anything new. They built the app around our existing menu and ordering system. Customers love it.' },
              { name: 'Sarah Thompson', restaurant: 'The Rustic Table', quote: 'Easy setup, great support, and our customers actually prefer ordering through our app now. Worth every penny of the $99.' },
            ].map((t, i) => (
              <div key={i}>
                <div className="speech-bubble">
                  <p style={{ color: '#333', lineHeight: 1.6, fontSize: '1rem', fontWeight: 500 }}>"{t.quote}"</p>
                </div>
                <div style={{ marginTop: '1.5rem', paddingLeft: '1rem' }}>
                  <div style={{ fontWeight: 800, color: '#000' }}>{t.name}</div>
                  <div style={{ fontSize: '0.875rem', color: '#6A0DAD', fontWeight: 600 }}>{t.restaurant}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section style={{ padding: '5rem 1.5rem', background: '#fff', borderBottom: '4px solid #000' }}>
        <div className="container">
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '1rem', color: '#000' }}>Apps & Websites We've Built</h2>
          <p style={{ textAlign: 'center', color: '#444', marginBottom: '3rem', fontWeight: 600, fontSize: '1.05rem' }}>Real restaurants running on HungrMates</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
            {[
              { name: 'QDOBA', image: 'https://hungrmates.weebly.com/uploads/1/3/5/7/135795236/81e50fc78a01d9197dd9221a5ec12666-orig_2.jpg' },
              { name: "Fazoli's", image: 'https://hungrmates.weebly.com/uploads/1/3/5/7/135795236/020bd59271a28b0c8ff8243a9d08068d-orig_2.jpg' },
              { name: 'Pot Belly', image: 'https://hungrmates.weebly.com/uploads/1/3/5/7/135795236/420e56f67776c91827e7ddd60e0e8c73-orig_2.jpg' },
              { name: 'Pizza House', image: 'https://hungrmates.weebly.com/uploads/1/3/5/7/135795236/f1a9548a7d5f950ec84032205755ce4a-orig_2.jpg' },
            ].map((client, i) => (
              <div key={i} className="comic-card" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }}>
                <div style={{ height: '280px', backgroundImage: `url(${client.image})`, backgroundSize: 'cover', backgroundPosition: 'center', borderBottom: '4px solid #000' }} />
                <div style={{ padding: '1rem', textAlign: 'center' }}>
                  <h3 style={{ fontWeight: 800, fontSize: '1.125rem', textTransform: 'uppercase' }}>{client.name}</h3>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <Link to="/portfolio" className="btn-secondary" style={{ fontSize: '1rem', padding: '12px 24px' }}>
              View Full Portfolio <ArrowRight size={18} style={{ marginLeft: '0.5rem' }} />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="halftone-bg" style={{ padding: '5rem 1.5rem', background: '#FFD700', textAlign: 'center', position: 'relative', borderBottom: '4px solid #000' }}>
        <div className="container" style={{ maxWidth: '700px', position: 'relative', zIndex: 1 }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem', color: '#000' }}>Ready to Own Your Orders?</h2>
          <p style={{ color: '#333', marginBottom: '2rem', fontSize: '1.125rem', fontWeight: 600 }}>
            Your restaurant deserves its own app and website. We'll build it around what you already use — for $99/mo.
          </p>
          <Link to="/book-demo" className="btn-primary" style={{ fontSize: '1.125rem', padding: '16px 32px', background: '#000', color: '#FFD700', border: '4px solid #000' }}>
            Book a Free Demo <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
          </Link>
          <p style={{ fontSize: '0.875rem', color: '#555', marginTop: '1rem', fontWeight: 600 }}>
            No credit card required. No contracts. Cancel anytime.
          </p>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          section > div > div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};
